/**
 * Counter API calls. Request and response shapes follow the implemented backend
 * (`/counter/*`, `/staff/floor`, `/staff/sessions`) exactly. Every amount is an integer in minor units,
 * calculated by the backend; nothing here adds, subtracts or derives money.
 *
 * Financial writes (payment, adjustment, print, close, open session) always carry the caller's
 * Idempotency-Key, and the client never repeats them on its own.
 */
import { staffApi } from '@/api/instances';
import type { ApiClient } from '@/api/client';

export interface FloorSession {
  session_id: string;
  party_display_name: string;
  party_size: number;
  status: string;
  duration_minutes: number;
  has_pending_orders: boolean;
  /** The bill's total as the backend last calculated it. */
  bill_total: number;
  has_active_assistance: boolean;
}

/** One open help request from `GET /staff/assistance`; only the fields the counter reads. */
export interface HelpRequest {
  request_id: string;
  type: string;
  status: string;
  session_id: string;
}

export interface FloorLocation {
  location_id: string;
  location_label: string;
  location_type: string;
  zone_id: string | null;
  zone_name: string | null;
  nominal_capacity: number;
  status: string;
  active_sessions: FloorSession[];
}

export interface BillLine {
  order_number: string;
  order_item_id: string;
  name: string;
  quantity: number;
  unit_amount: number;
  line_amount: number;
  modifiers: string[];
}

export interface BillAdjustment {
  adjustment_id: string;
  adjustment_type: string;
  /** Shown as a reduction; the backend sends the signed figure. */
  amount: number;
  reason_code: string | null;
  reason: string;
  created_at: string;
}

export interface BillPayment {
  payment_id: string;
  tender_type: string;
  amount: number;
  status: string;
  created_at: string;
}

/** `GET /counter/sessions/{id}/bill`. */
export interface Bill {
  bill_id: string;
  session_id: string;
  subtotal_amount: number;
  tax_amount: number;
  service_charge_amount: number;
  discount_amount: number;
  total_amount: number;
  paid_amount: number;
  remaining_balance: number;
  settlement_status: string;
  items: BillLine[];
  adjustments: BillAdjustment[];
  payments: BillPayment[];
}

export interface PaymentInput {
  tender_type: string;
  /** Integer minor units, exactly as entered. */
  amount: number;
  reference_notes?: string;
}

export interface PaymentResult {
  payment_id: string;
  bill_id: string;
  tender_type: string;
  amount: number;
  remaining_balance: number;
  settlement_status: string;
  created_at: string;
}

export type AdjustmentType = 'AUTHORIZED_DISCOUNT' | 'COMPLIMENTARY_DISCOUNT';

export interface AdjustmentInput {
  type: AdjustmentType;
  /** Integer minor units: the size of the reduction. */
  amount: number;
  reason_code: string;
  notes?: string;
}

export interface AdjustmentResult {
  adjustment_id: string;
  bill_id: string;
  adjustment_type: string;
  amount: number;
  revised_total_amount: number;
  remaining_balance: number;
}

export interface PrintResult {
  print_job_id: string;
  document_type: string;
  printer_id: string;
  is_reprint: boolean;
  reprint_indicator: string | null;
  reprint_counter: number;
  status: string;
}

export interface CloseInput {
  notes?: string;
  force?: boolean;
  reason?: string;
}

export interface CloseResult {
  session_id: string;
  location_id: string;
  location_label: string;
  session_status: string;
  location_status: string;
  closed_at: string;
  forced: boolean;
}

export interface OpenSessionInput {
  location_id: string;
  display_name: string;
  party_size: number;
}

export interface OpenedSession {
  session_id: string;
  location_id: string;
  location_label: string;
  status: string;
}

/**
 * The payment endpoint checks the tender type against the branch's accepted tenders before it touches the
 * bill, and a refusal lists them (`details.accepted_tenders`). No other call tells a cashier which tenders
 * the branch accepts, so this value can never be a real tender: asking with it returns the list and
 * changes nothing. Recorded as F3-DEC-02 / F3-LIM-01.
 */
export const TENDER_LIST_REQUEST = 'LIST_ACCEPTED_TENDERS';

export function createCounterApi(client: ApiClient) {
  return {
    getFloor: (signal?: AbortSignal) => client.get<FloorLocation[]>('/staff/floor', { signal }),

    /** The branch's open help requests, so a session card can say what was asked for. */
    getAssistance: (signal?: AbortSignal) =>
      client.get<HelpRequest[]>('/staff/assistance', { signal }),

    getBill: (sessionId: string, signal?: AbortSignal) =>
      client.get<Bill>(`/counter/sessions/${encodeURIComponent(sessionId)}/bill`, { signal }),

    recordPayment: (billId: string, input: PaymentInput, idempotencyKey: string) =>
      client.post<PaymentResult>(`/counter/bills/${encodeURIComponent(billId)}/payments`, input, {
        idempotencyKey,
      }),

    /** The refusal for an unknown tender carries the accepted list; see `TENDER_LIST_REQUEST`. */
    askAcceptedTenders: (billId: string, idempotencyKey: string) =>
      client.post<PaymentResult>(
        `/counter/bills/${encodeURIComponent(billId)}/payments`,
        { tender_type: TENDER_LIST_REQUEST, amount: 1 },
        { idempotencyKey },
      ),

    addAdjustment: (billId: string, input: AdjustmentInput, idempotencyKey: string) =>
      client.post<AdjustmentResult>(
        `/counter/bills/${encodeURIComponent(billId)}/adjustments`,
        input,
        { idempotencyKey },
      ),

    /** The first print of a bill is the original; later ones are reprints (the backend decides which). */
    printBill: (billId: string, idempotencyKey: string) =>
      client.post<PrintResult>(
        `/counter/bills/${encodeURIComponent(billId)}/print`,
        {},
        {
          idempotencyKey,
        },
      ),

    closeSession: (sessionId: string, input: CloseInput) =>
      client.post<CloseResult>(`/counter/sessions/${encodeURIComponent(sessionId)}/close`, input),

    openSession: (input: OpenSessionInput, idempotencyKey: string) =>
      client.post<OpenedSession>('/staff/sessions', input, { idempotencyKey }),
  };
}

export const counterApi = createCounterApi(staffApi);
